import { usePathname } from "@eurodrive/utils"
import { SearchPageMetadata } from "@eurodrive/utils/metadataTypes"
import {
  filterApplied,
  monthlyBudgetRangeChanged,
  searchDealTypeSelected,
  vehicleCategorySelected,
} from "@eurodrive/utils/gtm"
import debounce from "debounce"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { CarType, DealType, SearchFilters, defaultSearchFilters } from "./types"
import {
  areFiltersEqual,
  filtersToUrlParams,
  urlParamsToFilters,
} from "./urlUtils"

export type { CarType, DealType, SearchFilters } from "./types"

export interface SearchFiltersActions {
  setSearchQuery: (query: string) => void
  setCarType: (carType: CarType) => void
  setDealType: (dealType: DealType) => void
  setSelectedFeatures: (features: string[]) => void
  setSelectedCarTypes: (carTypes: string[]) => void
  setPriceRange: (range: [number, number]) => void
  setShowDiscountsOnly: (showDiscountsOnly: boolean) => void
  setSelectedManufacturer: (manufacturer: string) => void
  setSelectedModel: (model: string) => void
  setYearFrom: (year: string) => void
  setYearTo: (year: string) => void
  setKmFrom: (km: string) => void
  setKmTo: (km: string) => void

  // Compound actions
  toggleFeature: (featureId: string) => void
  toggleCarType: (carTypeId: string) => void
  handleManufacturerChange: (e: any) => void
  handleModelChange: (e: any) => void
  handleSliderChange: (
    event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => void
}

export { defaultSearchFilters }

export function useSearchFilters(
  serverInitialFilters?: SearchFilters,
  metadata?: SearchPageMetadata,
  /** Smart default carType for unfiltered leasing URLs (determined server-side) */
  smartDefaultCarType?: CarType,
) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const isInitialized = useRef(false)
  const isUpdatingFromUrl = useRef(false)

  // Helper to get metadata for a specific carType and dealType
  const getMetadataForFilters = useCallback(
    (
      carType: CarType,
      dealType: DealType,
    ): { minimumPrice: number; maximumPrice: number } | null => {
      if (!metadata) return null
      const metadataSet =
        dealType === "leasing" ? metadata.leasing : metadata.sale[dealType]
      return metadataSet[carType]
    },
    [metadata],
  )

  // Clamp a price range parsed from the URL to the metadata bounds of the
  // filters' carType/dealType. Price params can be stale relative to the active
  // category (old links, or a debounced URL write racing a category switch),
  // which leaves the slider thumbs pinned outside their own min/max.
  const clampPriceRangeToMetadata = (parsed: SearchFilters): SearchFilters => {
    const relevantMetadata = getMetadataForFilters(
      parsed.carType,
      parsed.dealType,
    )
    if (!relevantMetadata) return parsed

    const clampedMin = Math.max(
      parsed.priceRange[0],
      relevantMetadata.minimumPrice,
    )
    const clampedMax = Math.min(
      parsed.priceRange[1],
      relevantMetadata.maximumPrice,
    )
    parsed.priceRange =
      clampedMin <= clampedMax
        ? [clampedMin, clampedMax]
        : [relevantMetadata.minimumPrice, relevantMetadata.maximumPrice]
    return parsed
  }

  // Initialize filters from URL on first load
  const [filters, setFilters] = useState<SearchFilters>(() => {
    // Otherwise, parse URL params on client-side to avoid hydration issues
    if (typeof window !== "undefined" && searchParams) {
      const parsed = urlParamsToFilters(searchParams)

      // Apply smart default carType for unfiltered leasing URLs
      // This happens when URL is just ?dealType=leasing with no carType specified
      if (
        smartDefaultCarType &&
        parsed.dealType === "leasing" &&
        parsed.carType === defaultSearchFilters.carType
      ) {
        // Check if carType was NOT explicitly in URL (would still be default)
        const urlCarType = searchParams.get("carType")
        if (!urlCarType) {
          parsed.carType = smartDefaultCarType
        }
      }

      // Override default price range with metadata values if provided and no price in URL
      if (
        metadata &&
        parsed.priceRange[0] === 0 &&
        parsed.priceRange[1] === 0
      ) {
        const relevantMetadata = getMetadataForFilters(
          parsed.carType,
          parsed.dealType,
        )
        if (relevantMetadata) {
          parsed.priceRange = [
            relevantMetadata.minimumPrice,
            relevantMetadata.maximumPrice,
          ]
        }
      }
      return clampPriceRangeToMetadata(parsed)
    }
    // Use metadata values for defaults if provided
    const defaults = { ...defaultSearchFilters }
    if (smartDefaultCarType) {
      defaults.carType = smartDefaultCarType
    }
    if (metadata) {
      const relevantMetadata = getMetadataForFilters(
        defaults.carType,
        defaults.dealType,
      )
      if (relevantMetadata) {
        defaults.priceRange = [
          relevantMetadata.minimumPrice,
          relevantMetadata.maximumPrice,
        ]
      }
    }
    return defaults
  })

  // Debounced filters state for URL updates and API calls
  const [debouncedFilters, setDebouncedFilters] =
    useState<SearchFilters>(filters)

  // Create a debounced function to update debouncedFilters
  const debouncedUpdateFilters = useMemo(
    () =>
      debounce((newFilters: SearchFilters) => {
        setDebouncedFilters(newFilters)
      }, 1000),
    [],
  )

  // Update debouncedFilters when filters change
  useEffect(() => {
    debouncedUpdateFilters(filters)
  }, [filters, debouncedUpdateFilters])

  // Update URL when filters change (with debouncing)
  const updateUrl = useCallback(
    (newFilters: SearchFilters) => {
      if (isUpdatingFromUrl.current) return

      // Pass the active category bounds so an untouched (full-range) slider
      // keeps the URL free of priceMin/priceMax (END-67)
      const params = filtersToUrlParams(
        newFilters,
        getMetadataForFilters(newFilters.carType, newFilters.dealType),
      )
      const paramsString = params.toString()
      const newUrl = paramsString ? `${pathname}?${paramsString}` : pathname

      // Use replace to avoid adding too many history entries
      router.replace(newUrl, { scroll: false })
    },
    [router, pathname, getMetadataForFilters],
  )

  // Listen for URL changes (browser back/forward)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true
      return
    }

    if (searchParams) {
      isUpdatingFromUrl.current = true
      const newFilters = urlParamsToFilters(searchParams)

      // If price range is [0, 0] (no price in URL), preserve current price range
      // This happens when URL has carType but no explicit price params
      if (newFilters.priceRange[0] === 0 && newFilters.priceRange[1] === 0) {
        newFilters.priceRange = filters.priceRange
      }
      clampPriceRangeToMetadata(newFilters)

      if (!areFiltersEqual(filters, newFilters)) {
        setFilters(newFilters)
      }

      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromUrl.current = false
      }, 0)
    }
  }, [searchParams])

  // Update URL when debouncedFilters change (debounced by 300ms)
  useEffect(() => {
    if (isInitialized.current && !isUpdatingFromUrl.current) {
      updateUrl(debouncedFilters)
    }
  }, [debouncedFilters, updateUrl])

  const actions: SearchFiltersActions = {
    setSearchQuery: useCallback((searchQuery: string) => {
      setFilters((prev) => ({ ...prev, searchQuery }))
    }, []),

    setCarType: useCallback((carType: CarType) => {
      setFilters((prev) => ({ ...prev, carType }))
      vehicleCategorySelected(carType)
    }, []),

    setDealType: useCallback((dealType: DealType) => {
      setFilters((prev) => ({ ...prev, dealType }))
      // Map dealType to match CSV parameter values
      const dealTypeMapping: Record<DealType, string> = {
        new: "purchase",
        used: "purchase",
        leasing: "leasing_business", // Default to business, can be refined
      }
      searchDealTypeSelected(dealTypeMapping[dealType])
    }, []),

    setSelectedFeatures: useCallback((selectedFeatures: string[]) => {
      setFilters((prev) => ({ ...prev, selectedFeatures }))
    }, []),

    setSelectedCarTypes: useCallback((selectedCarTypes: string[]) => {
      setFilters((prev) => ({ ...prev, selectedCarTypes }))
    }, []),

    setPriceRange: useCallback((priceRange: [number, number]) => {
      setFilters((prev) => ({ ...prev, priceRange }))
    }, []),

    setShowDiscountsOnly: useCallback((showDiscountsOnly: boolean) => {
      setFilters((prev) => ({ ...prev, showDiscountsOnly }))
      if (showDiscountsOnly) {
        filterApplied("discount", "true")
      }
    }, []),

    setSelectedManufacturer: useCallback((selectedManufacturer: string) => {
      setFilters((prev) => ({
        ...prev,
        selectedManufacturer,
        selectedModel: "", // Reset model when manufacturer changes
      }))
    }, []),

    setSelectedModel: useCallback((selectedModel: string) => {
      setFilters((prev) => ({ ...prev, selectedModel }))
    }, []),

    setYearFrom: useCallback((yearFrom: string) => {
      setFilters((prev) => ({ ...prev, yearFrom }))
    }, []),

    setYearTo: useCallback((yearTo: string) => {
      setFilters((prev) => ({ ...prev, yearTo }))
    }, []),

    setKmFrom: useCallback((kmFrom: string) => {
      setFilters((prev) => ({ ...prev, kmFrom }))
    }, []),

    setKmTo: useCallback((kmTo: string) => {
      setFilters((prev) => ({ ...prev, kmTo }))
    }, []),

    // Compound actions
    toggleFeature: useCallback((featureId: string) => {
      setFilters((prev) => {
        const isAdding = !prev.selectedFeatures.includes(featureId)
        if (isAdding) {
          filterApplied("feature", featureId)
        }
        return {
          ...prev,
          selectedFeatures: isAdding
            ? [...prev.selectedFeatures, featureId]
            : prev.selectedFeatures.filter((id) => id !== featureId),
        }
      })
    }, []),

    toggleCarType: useCallback((carTypeId: string) => {
      setFilters((prev) => {
        // Check if clicking on already selected car type (deselect)
        const isAlreadySelected = prev.selectedCarTypes.includes(carTypeId)

        // Track filter applied only when selecting a new car type
        if (!isAlreadySelected) {
          filterApplied("car_type", carTypeId)
        }

        return {
          ...prev,
          // Single selection: replace with new selection, or empty if deselecting
          selectedCarTypes: isAlreadySelected ? [] : [carTypeId],
        }
      })
    }, []),

    handleManufacturerChange: useCallback((e: any) => {
      const selectedManufacturer = e.target.value
      if (selectedManufacturer) {
        filterApplied("manufacturer", selectedManufacturer)
      }
      setFilters((prev) => ({
        ...prev,
        selectedManufacturer,
        selectedModel: "", // Reset model when manufacturer changes
      }))
    }, []),

    handleModelChange: useCallback((e: any) => {
      const selectedModel = e.target.value
      if (selectedModel) {
        filterApplied("model", selectedModel)
      }
      setFilters((prev) => ({ ...prev, selectedModel }))
    }, []),

    handleSliderChange: useCallback(
      (_: Event, newValue: number | number[], activeThumb: number) => {
        if (!Array.isArray(newValue)) {
          return
        }

        const minDistance = 0

        setFilters((prev) => {
          let newRange: [number, number]
          if (activeThumb === 0) {
            // Moving the minimum thumb
            newRange = [
              Math.min(newValue[0], prev.priceRange[1] - minDistance),
              prev.priceRange[1],
            ]
          } else {
            // Moving the maximum thumb
            newRange = [
              prev.priceRange[0],
              Math.max(newValue[1], prev.priceRange[0] + minDistance),
            ]
          }

          // Track budget range change
          const minBudget = Math.min(...prev.priceRange)
          const maxBudget = Math.max(...prev.priceRange)
          monthlyBudgetRangeChanged(
            String(minBudget),
            String(maxBudget),
            String(newRange[0]),
            String(newRange[1]),
          )

          return {
            ...prev,
            priceRange: newRange,
          }
        })
      },
      [],
    ),
  }

  return {
    filters,
    debouncedFilters,
    actions,
  }
}
